import React, { useContext, useEffect } from "react";

// Third-party
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";

// Utils
import getenvvar from "utils/Env";

const DispatchContext = React.createContext();
const StateContext = React.createContext();

const PRODUCT_FRUITS_WORKSPACE_CODE = getenvvar(
  "REACT_APP_PRODUCT_FRUITS_WORKSPACE_CODE"
);
const PRODUCT_FRUITS_TENANT = getenvvar("REACT_APP_API_BASE_URL");

function getProductFruitsUserInfo({
  userName,
  userType,
  hmacCode,
  profileId,
  companyId,
  companySubscription,
  companyGeography
}) {
  return {
    username: userName,
    role: userType,
    hmac: {
      hash: hmacCode,
      expiration: null
    },
    props: {
      id: profileId,
      companyId: companyId,
      tenant: PRODUCT_FRUITS_TENANT,
      companySubscription: companySubscription,
      companyGeography: companyGeography
    }
  };
}

/**
 * Initial state for the ProductFruits Context
 */
const initialState = {
  userInfo: null
};

/**
 * Reducer with the actions to update the ProductFruits Context state.
 * @param { object } state
 * @param { object } action
 * @returns
 */
function ProductFruitsReducer(state, action) {
  switch (action.type) {
    case "SET_USER_INFO": {
      return {
        ...state,
        userInfo: action.payload.userInfo
      };
    }

    default: {
      return state;
    }
  }
}

/**
 * Provides the ProductFruits context to the application.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {ReactNode} props.children - The child components.
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated.
 * @param {string} props.profileId - The user's profile ID.
 * @param {string} props.userType - The user's type.
 * @param {string} props.userName - The user's name.
 * @param {string} props.companyId - The user's company ID.
 * @param {string} props.companySubscription - The company's subscription type.
 * @param {string} props.companyGeography - The company's sales geography.
 * @param {string} props.hmacCode - The user's hmac to use in ProductFruits
 * @returns {ReactNode} The rendered component.
 */
function ProductFruitsProvider({
  children,
  isAuthenticated,
  profileId,
  userType,
  userName,
  companyId,
  companySubscription,
  companyGeography,
  hmacCode
}) {
  const [state, dispatch] = React.useReducer(
    ProductFruitsReducer,
    initialState
  );
  const location = useLocation();

  useEffect(() => {
    window.$productFruits.push(["pageChanged"]);
  }, [location]);

  /**
   * Effect to initialize the ProductFruits instance only if the user is authenticated
   * and the cookie consent state is accepted
   */
  useEffect(() => {
    if (!PRODUCT_FRUITS_WORKSPACE_CODE) {
      console.warn(
        "ProductFruits token not found. Make sure REACT_APP_PRODUCT_FRUITS_WORKSPACE_CODE is set in the .env file."
      );
      return;
    }

    // If userInfo is set and the user is not authenticated, we need to destroy the instance and set userInfo to null
    if (state.userInfo && isAuthenticated === false) {
      try {
        window.productFruits.services.destroy();
      } catch (error) {
        console.error("Error destroying ProductFruits instance", error);
      }

      dispatch({
        type: "SET_USER_INFO",
        payload: { userInfo: null }
      });
      return;
    }

    // If userInfo is set and the user is authenticated, we don't need to do anything
    if (state.userInfo && isAuthenticated) {
      return;
    }

    // If userInfo is not set and the user is not authenticated, we can't do anything
    if (!state.userInfo && isAuthenticated === false) {
      return;
    }

    // If userInfo is not set and the user is authenticated, we need to initialize the instance and
    // set the userInfo
    if (!state.userInfo && isAuthenticated) {
      // The way of initializing the instance is just setting the userInfo
      // since that will trigger mounting the component
      const productFruitsUserInfo = getProductFruitsUserInfo({
        userName,
        userType,
        hmacCode,
        profileId,
        companyId,
        companySubscription,
        companyGeography
      });

      dispatch({
        type: "SET_USER_INFO",
        payload: {
          userInfo: productFruitsUserInfo
        }
      });

      window.$productFruits.push([
        "init",
        PRODUCT_FRUITS_WORKSPACE_CODE,
        "en",
        productFruitsUserInfo,
        {
          disableLocationChangeDetection: true,
          hideInAppCenterLauncher: true
        }
      ]);

      return;
    }
  }, [
    state.isInitialized,
    isAuthenticated,
    profileId,
    userType,
    userName,
    companyId,
    companySubscription,
    companyGeography,
    state.userInfo,
    hmacCode
  ]);

  return (
    <DispatchContext.Provider value={dispatch}>
      <StateContext.Provider value={{ ...state }}>
        {children}
      </StateContext.Provider>
    </DispatchContext.Provider>
  );
}

ProductFruitsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  profileId: PropTypes.number.isRequired,
  userType: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
  companyId: PropTypes.number.isRequired,
  companySubscription: PropTypes.string,
  companyGeography: PropTypes.string,
  hmacCode: PropTypes.string.isRequired
};

/**
 * Custom hook that provides access to the ProductFruits context.
 */
function useProductFruitsContext() {
  const productFruitsState = useContext(StateContext);
  return productFruitsState;
}

export { useProductFruitsContext };

export default ProductFruitsProvider;
